import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import Navbar from '../homepage/components/Navbar.jsx';
import eclipseerlogo from './eclipseer-logo.png';
import '../static/css/layout.css';
import '../static/css/account.css';
import accountBg from '../static/assets/account_bg.png';
import { authAPI } from '../../services/apiClient';
import { clearLocalStorageExcept } from '../../utils/storage';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Rate limit handling states
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();

  const leftContentRef = useRef(null);
  const formContainerRef = useRef(null);

  useEffect(() => {
    let currentIndex = 1;
    const interval = setInterval(() => {
      currentIndex = (currentIndex % 3) + 1;
      showContent(currentIndex);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Rate limit handling - listen for 'rateLimited' event
  useEffect(() => {
    const handleRateLimited = (event) => {
      const { retryAfter } = event.detail;
      setCountdown(retryAfter);
      setIsRateLimited(true);
    };

    window.addEventListener('rateLimited', handleRateLimited);

    return () => {
      window.removeEventListener('rateLimited', handleRateLimited);
    };
  }, []);

  // Countdown timer for rate limit
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && isRateLimited) {
      setIsRateLimited(false);
    }
  }, [countdown, isRateLimited]);

  useEffect(() => {
    // GSAP animation setup
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    // Set initial states - both elements start off-screen
    gsap.set(leftContentRef.current, { x: "-100%", opacity: 0 });
    gsap.set(formContainerRef.current, { x: "100%", opacity: 0 });

    // Animation sequence
    tl.to(leftContentRef.current, {
      x: 0,
      opacity: 1,
      duration: 1.2,
      ease: "power2.out"
    })
    .to(formContainerRef.current, {
      x: 0,
      opacity: 1,
      duration: 1,
      ease: "power2.out"
    }, "-=0.8"); // Start before first animation finishes for overlap

    // Cleanup function
    return () => {
      tl.kill();
    };
  }, []);

  const showContent = (index) => {
    document.querySelectorAll('.eclipseer_unique_tab_button').forEach(button => 
      button.classList.remove('eclipseer_active')
    );
    document.querySelectorAll('.eclipseer_unique_tab_button')[index - 1].classList.add('eclipseer_active');
    
    document.querySelectorAll('.eclipseer_unique_content_item').forEach(item => 
      item.classList.remove('eclipseer_active')
    );
    document.getElementById(`eclipseer_content_${index}`).classList.add('eclipseer_active');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const completeLogin = (data) => {
    // Store token and user info
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('userRole', data.role);

    // Navigate based on role
    if (data.role === 'superadmin') {
      navigate('/admin');
      return;
    }

    if (data.role === 'business_admin') {
      const businessId = data.user?.business_id;
      if (!businessId) {
        setError("Business Admin not associated with a business. Contact support.");
        clearLocalStorageExcept();
        return;
      }
      navigate('/business-admin/dashboard');
      return;
    }

    // Default fallback
    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      setError('Please enter username/email and password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Pass credentials as an object with username field (backend accepts username or email in 'username' field)
      const loginData = {
        username: username,
        password: password,
      };

      const response = await authAPI.login(loginData);
      const data = response.data;

      await completeLogin(data);

    } catch (err) {
      console.error('Login Error:', err);
      let specificError = 'Sign In failed. Please try again.';
      if (err.response && err.response.data && err.response.data.error) {
        specificError = err.response.data.error;
      } else if (err.message) {
        specificError = err.message;
      }
      setError(specificError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="account_page">
      <Navbar currentPage="home" setPage={() => {}} />
      <div
        className="main_container container_width"
        style={{ paddingTop: '120px' }}
      >
        <div className="extra_info" ref={leftContentRef}>
          <img src={accountBg} alt="background" />
          <div
            className="extra_info_content"
            style={{ transform: 'translate(-50%, calc(-50% + 20px))' }}
          >
            <div className="extra_info_one">
              <img src={eclipseerlogo} alt="eclipseer logo" />
              <h3 className="primaryfont">Welcome Back</h3>
              <p style={{ textAlign: 'center' }}>
                Create surveys, collect feedback, and get insights, faster with AI.
              </p>
            </div>

            <div className="extra_info_two">
              <img src={eclipseerlogo} alt="eclipseer logo" />
              <h3 className="primaryfont">Why Us?</h3>
              <div className="eclipseer_unique_button_container">
                {[1, 2, 3].map(num => (
                  <button
                    key={num}
                    className={`primaryfont eclipseer_unique_tab_button ${num === 1 ? 'eclipseer_active' : ''}`}
                    onClick={() => showContent(num)}
                  >
                    {num}
                  </button>
                ))}
              </div>
              <div className="eclipseer_unique_content_section">
                <div id="eclipseer_content_1" className="eclipseer_unique_content_item eclipseer_active">
                  Eclipseer bridges the gap between businesses seeking actionable insights and participants looking to earn rewards.
                </div>
                <div id="eclipseer_content_2" className="eclipseer_unique_content_item">
                  With cutting-edge AI, we make the process seamless and impactful.
                </div>
                <div id="eclipseer_content_3" className="eclipseer_unique_content_item">
                  Rewarding for all parties involved with meaningful participation.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="form_container"
          ref={formContainerRef}
          style={{ color: 'white', transform: 'translateY(-100px)' }}
        >
          <h2 className="form_heading primaryfont" style={{ marginBottom: '2rem' }}>
            Sign In
          </h2>
          {error && <div id="login_message" style={{ whiteSpace: 'pre-line' }}>{error}</div>}

          {isRateLimited && (
            <div className="rate_limit_message" style={{
              backgroundColor: '#fff3cd',
              color: '#856404',
              padding: '10px',
              marginBottom: '1rem',
              borderRadius: '4px',
              textAlign: 'center'
            }}>
              Too many login attempts. Please try again in {countdown} seconds.
            </div>
          )}

          <form id="loginForm" onSubmit={handleSubmit}>
            <div className="input_container">
              <input
                className="primaryfont"
                type="text"
                id="loginIdentifier"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username or Email*"
                required
                disabled={isRateLimited}
              />
            </div>

            <div className="password_container">
              <div>
                <input
                  className="primaryfont"
                  type={showPassword ? 'text' : 'password'}
                  id="loginPassword"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password*"
                  required
                  disabled={isRateLimited}
                />
                <button type="button" onClick={togglePasswordVisibility}>
                  <i className={`ri-eye${showPassword ? '-off' : ''}-fill ri-xl`}></i>
                </button>
              </div>
            </div>

            <div className="multi_option_container">
              <div className="remember_me_contanier">
                <input type="checkbox" id="loginRememberMe" />
                <span>Remember Me</span>
              </div>
              <div className="form_button_type_1">
                <button type="button" onClick={() => navigate('/forgot-password')}>
                  Forgot Password?
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="form_button_type_2 primaryfont"
              disabled={isLoading || isRateLimited}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
